// frontend/src/components/leads/EditLead.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import Layout from "../common/Layout";
import DmsSelector from "../dms/DmsSelector";
import ProductSelector from "../products/ProductSelector";
import ContactList from "../contacts/ContactList";
import DocumentUpload from "../documents/DocumentUpload";
import DocumentList from "../documents/DocumentList";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

const emptyContact = () => ({
  firstName: "",
  lastName: "",
  title: "",
  workPhone: "",
  homePhone: "",
  email: "",
  mobilePhone: "",
});

const asArray = (value) => (Array.isArray(value) ? value : []);
const val = (obj, camel, snake, fallback = "") =>
  obj?.[camel] ?? obj?.[snake] ?? fallback;

const SectionHeader = ({ number, title, required = false }) => (
  <div className="form-section-header">
    <span className="section-number">{number}</span>
    <h3>
      {title}
      {required && <span className="required">*</span>}
    </h3>
  </div>
);

function EditLead() {
  const { id: routeLeadId } = useParams();
  const id = routeLeadId;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [leadTypes, setLeadTypes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [serviceDashboards, setServiceDashboards] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [salesRepName, setSalesRepName] = useState("");
  const [manufacturers, setManufacturers] = useState([]);
  const [dealershipExpanded, setDealershipExpanded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);

  const [formData, setFormData] = useState({
    company: "",
    salesRepId: "",
    dmsProviders: [],
    otherDmsProvider: "",
    dealerGroup: "",
    manufacturer: [],
    rooftops: null,
    mainLine: "",
    email: "",
    website: "",
    streetAddress: "",
    state: "",
    city: "",
    zipCode: "",
    country: "USA",
    leadSource: "None",
    leadType: "New",
    serviceDashboard: "None",
    contactDate: "",
    opportunityYn: null,
    status: "New",
    products: [],
    notes: "",
    nextSteps: "",
    contacts: [emptyContact()],
  });

  useEffect(() => {
    loadMasters();
  }, [user?.role]);

  useEffect(() => {
    if (id) {
      fetchLead();
    }
  }, [id]);

  const loadMasters = async () => {
    try {
      const [types, sources, dashboards, mfrs, statuses] = await Promise.all([
        api.getMasterData("leadTypes"),
        api.getMasterData("leadSources"),
        api.getMasterData("serviceDashboards"),
        api.getMasterData("manufacturers"),
        api.getMasterData("leadStatuses"),
      ]);
      setLeadTypes(Array.isArray(types) ? types : []);
      setLeadSources(Array.isArray(sources) ? sources : []);
      setServiceDashboards(Array.isArray(dashboards) ? dashboards : []);
      setManufacturers(Array.isArray(mfrs) ? mfrs : []);
      setLeadStatuses(Array.isArray(statuses) ? statuses : []);
      if (
        ["SM", "Admin", "Sales Owner", "Sales Manager"].includes(user?.role)
      ) {
        api
          .getSalesReps()
          .then((reps) => setSalesReps(Array.isArray(reps) ? reps : []))
          .catch(console.error);
      }
    } catch (e) {
      console.error("Master data load failed", e);
    }
  };

  const fetchLead = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getLead(id);
      const contacts = asArray(data.contacts).map((c) => ({
        firstName: val(c, "firstName", "first_name"),
        lastName: val(c, "lastName", "last_name"),
        title: val(c, "title", "title"),
        workPhone: val(c, "workPhone", "work_phone"),
        homePhone: val(c, "homePhone", "home_phone"),
        email: val(c, "email", "email"),
        mobilePhone: val(c, "mobilePhone", "mobile_phone"),
      }));
      const rawDms = asArray(
        data.dmsProviders?.length ? data.dmsProviders : data.dms_providers,
      );
      const other = val(data, "otherDmsProvider", "other_dms_provider", "");

      let rooftops = data.rooftops;
      if (rooftops === "" || rooftops === null || rooftops === undefined) {
        rooftops = null;
      } else {
        rooftops = Number(rooftops);
        if (isNaN(rooftops)) rooftops = null;
      }

      setSalesRepName(
        data.partner_name ||
          data.sales_rep_name ||
          data.salesRepName ||
          data.partnerName ||
          "",
      );

      setFormData({
        company: val(data, "company", "company"),
        salesRepId: data.partner_id || "",
        dmsProviders: rawDms.filter(
          (v) =>
            v !== "Other" &&
            !(typeof v === "string" && v.startsWith("Other: ")),
        ),
        otherDmsProvider:
          other ||
          rawDms
            .find((v) => typeof v === "string" && v.startsWith("Other: "))
            ?.substring(7) ||
          "",
        dealerGroup: val(data, "dealerGroup", "dealer_group"),
        manufacturer: asArray(data.manufacturer),
        rooftops: rooftops,
        mainLine: val(data, "mainLine", "main_line"),
        email: data.email || "",
        website: data.website || "",
        streetAddress: val(data, "streetAddress", "street_address"),
        state: data.state || "",
        city: data.city || "",
        zipCode: val(data, "zipCode", "zip_code"),
        country: data.country || "USA",
        leadSource: val(data, "leadSource", "lead_source", "None"),
        leadType: val(data, "leadType", "lead_type", "New"),
        serviceDashboard: val(
          data,
          "serviceDashboard",
          "service_dashboard",
          "None",
        ),
        contactDate: (() => {
          const value = val(data, "contactDate", "contact_date");
          return value ? value.substring(0, 10) : "";
        })(),
        opportunityYn: val(data, "opportunityYn", "opportunity_yn", null),
        status: data.status || "New",
        products: asArray(data.products),
        notes: data.notes || "",
        nextSteps: val(data, "nextSteps", "next_steps"),
        contacts: contacts.length ? contacts : [emptyContact()],
      });

      setDocuments(data.documents || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || "Failed to load lead data");
    } finally {
      setLoading(false);
    }
  };

  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e, bookDemoAfterSave = false) => {
    e.preventDefault();
    if (!formData.company.trim())
      return setError("Dealership name is required");
    if (!formData.dmsProviders.length && !formData.otherDmsProvider.trim()) {
      return setError("At least one DMS Provider is required");
    }
    const validContacts = formData.contacts.filter((c) => c.firstName?.trim());
    if (!validContacts.length)
      return setError("At least one contact with a name is required");

    setSubmitting(true);
    setError(null);
    try {
      let rooftops = formData.rooftops;
      if (
        rooftops === "" ||
        rooftops === null ||
        rooftops === undefined ||
        isNaN(rooftops)
      ) {
        rooftops = null;
      } else {
        rooftops = Number(rooftops);
      }

      const ownerRoles = ["SM", "Admin", "Sales Owner", "Sales Manager"];
      const leadUpdatePayload = {
        company: formData.company.trim(),
        dmsProviders: formData.dmsProviders,
        otherDmsProvider: formData.otherDmsProvider.trim(),
        dealerGroup: formData.dealerGroup,
        manufacturer: formData.manufacturer,
        rooftops: rooftops,
        mainLine: formData.mainLine,
        email: formData.email,
        website: formData.website,
        streetAddress: formData.streetAddress,
        state: formData.state,
        city: formData.city,
        zipCode: formData.zipCode,
        country: formData.country,
        leadSource: formData.leadSource,
        leadType: formData.leadType,
        serviceDashboard: formData.serviceDashboard,
        contactDate: formData.contactDate || null,
        opportunityYn: formData.opportunityYn || null,
        status: formData.status,
        products: formData.products,
        notes: formData.notes,
        nextSteps: formData.nextSteps,
        contacts: validContacts.map((c) => ({
          firstName: c.firstName,
          lastName: c.lastName,
          title: c.title,
          workPhone: c.workPhone,
          homePhone: c.homePhone,
          email: c.email,
          mobilePhone: c.mobilePhone,
        })),
      };

      if (ownerRoles.includes(String(user?.role || "").trim())) {
        leadUpdatePayload.salesRepId = formData.salesRepId || undefined;
      }

      await api.updateLead(id, leadUpdatePayload);

      if (bookDemoAfterSave) {
        navigate(`/leads/${id}/demo`);
        return;
      }
      navigate("/leads");
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || "Failed to update lead");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDealership = () => {
    setDealershipExpanded((prev) => !prev);
  };

  const refreshAfterChange = async () => {
    await fetchLead();
  };

  const handleSaveAndBookDemo = async () => {
    const fakeEvent = { preventDefault: () => {} };
    await handleSubmit(fakeEvent, true);
  };

  if (error && !formData.company) {
    return (
      <Layout title="Edit Lead" subtitle="Unable to load lead information">
        <ErrorMessage message={error} onRetry={fetchLead} />
      </Layout>
    );
  }

  const formContent = (
    <>
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="lead-form">
        {/* ============================================================
            SECTION 1: DEALERSHIP
            ============================================================ */}
        <div className="form-section">
          <SectionHeader number="1" title="Dealership" required />

          <div className="form-group">
            <label>
              Dealership Name <span className="required">*</span>
            </label>
            <input
              className="form-control"
              value={formData.company}
              onChange={(e) => setField("company", e.target.value)}
              required
            />
          </div>

          <DmsSelector
            selected={[
              ...formData.dmsProviders,
              ...(formData.otherDmsProvider
                ? [`Other: ${formData.otherDmsProvider}`]
                : []),
            ]}
            onChange={(v) => {
              const arr = asArray(v);
              setField(
                "dmsProviders",
                arr.filter(
                  (x) => !String(x).startsWith("Other: ") && x !== "Other",
                ),
              );
              const other = arr.find((x) => String(x).startsWith("Other: "));
              setField(
                "otherDmsProvider",
                other ? String(other).substring(7) : "",
              );
            }}
          />

          <div className="form-group">
            <label>Sales Agent / Owner</label>
            {["SM", "Admin", "Sales Owner", "Sales Manager"].includes(
              user?.role,
            ) ? (
              <select
                className="form-control"
                value={formData.salesRepId}
                onChange={(e) => setField("salesRepId", e.target.value)}
              >
                {user?.userId && (
                  <option value={String(user.userId)}>
                    {user.fullName || "Logged-in User"} (Me)
                  </option>
                )}
                {salesReps
                  .filter((rep) => String(rep.user_id) !== String(user?.userId))
                  .map((rep) => (
                    <option key={rep.user_id} value={rep.user_id}>
                      {rep.full_name}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                className="form-control"
                value={salesRepName || user?.fullName || ""}
                readOnly
              />
            )}
            <small className="text-muted">
              Ownership is stored using the CRM User ID (partner_id).
            </small>
          </div>

          <button
            type="button"
            className="btn-toggle-details"
            onClick={toggleDealership}
          >
            <i
              className={`fas fa-${dealershipExpanded ? "minus" : "plus"}`}
            ></i>
            <span>
              {dealershipExpanded ? "Hide" : "Add"} dealership details
            </span>
            <span className="optional-text">(Optional)</span>
          </button>

          {dealershipExpanded && (
            <div className="dealership-details-panel">
              <div className="form-group">
                <label>Dealer Group</label>
                <input
                  className="form-control"
                  value={formData.dealerGroup}
                  onChange={(e) => setField("dealerGroup", e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Manufacturer</label>
                <div className="checkbox-group">
                  {manufacturers.map((m) => (
                    <label key={m.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.manufacturer.includes(m.name)}
                        onChange={() =>
                          setField(
                            "manufacturer",
                            formData.manufacturer.includes(m.name)
                              ? formData.manufacturer.filter(
                                  (x) => x !== m.name,
                                )
                              : [...formData.manufacturer, m.name],
                          )
                        }
                      />
                      {m.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label># of Rooftops</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.rooftops === null ? "" : formData.rooftops}
                    onChange={(e) => {
                      const val = e.target.value;
                      setField("rooftops", val === "" ? null : Number(val));
                    }}
                    placeholder="Optional"
                    min="1"
                  />
                  <small className="text-muted">
                    Leave empty if not applicable
                  </small>
                </div>
                <div className="form-group">
                  <label>Dealer Main Line</label>
                  <input
                    className="form-control"
                    value={formData.mainLine}
                    onChange={(e) => setField("mainLine", e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Dealership Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) => setField("email", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input
                    className="form-control"
                    value={formData.website}
                    onChange={(e) => setField("website", e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Street Address</label>
                <input
                  className="form-control"
                  value={formData.streetAddress}
                  onChange={(e) => setField("streetAddress", e.target.value)}
                  placeholder="1450 Ogden Ave"
                />
                <small className="text-muted">
                  Optional — it helps the team place the store
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    className="form-control"
                    value={formData.city}
                    onChange={(e) => setField("city", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    className="form-control"
                    value={formData.state}
                    onChange={(e) => setField("state", e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ZIP</label>
                  <input
                    className="form-control"
                    value={formData.zipCode}
                    onChange={(e) => setField("zipCode", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    className="form-control"
                    value={formData.country}
                    onChange={(e) => setField("country", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ============================================================
            SECTION 2: LEAD INFORMATION
            ============================================================ */}
        <div className="form-section">
          <SectionHeader number="2" title="Lead Information" />

          <div className="form-row">
            <div className="form-group">
              <label>Lead Status</label>
              <select
                className="form-control"
                value={formData.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                {leadStatuses.map((x) => (
                  <option key={x.id || x.name} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Lead Type</label>
              <select
                className="form-control"
                value={formData.leadType}
                onChange={(e) => setField("leadType", e.target.value)}
              >
                {leadTypes.map((x) => (
                  <option key={x.id} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lead Source</label>
              <select
                className="form-control"
                value={formData.leadSource}
                onChange={(e) => setField("leadSource", e.target.value)}
              >
                <option value="">Select Lead Source</option>
                {leadSources.map((x) => (
                  <option key={x.id} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Choose Service Dashboard</label>
              <select
                className="form-control"
                value={formData.serviceDashboard}
                onChange={(e) => setField("serviceDashboard", e.target.value)}
              >
                {serviceDashboards.map((x) => (
                  <option key={x.id} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.contactDate}
                onChange={(e) => setField("contactDate", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Opportunity</label>
              <select
                className="form-control"
                value={formData.opportunityYn ?? ""}
                onChange={(e) =>
                  setField("opportunityYn", e.target.value || null)
                }
              >
                <option value="">Not Set</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Next Steps</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.nextSteps}
              onChange={(e) => setField("nextSteps", e.target.value)}
              placeholder="What should happen next?"
              maxLength="2000"
            />
          </div>
        </div>

        {/* ============================================================
            SECTION 3: CONTACTS
            ============================================================ */}
        <div className="form-section">
          <SectionHeader number="3" title="Contacts" required />

          <p className="section-desc">
            At least one contact with a name is required
          </p>

          <ContactList
            contacts={formData.contacts}
            onChange={(v) => setField("contacts", v)}
          />
        </div>

        {/* ============================================================
            SECTION 4: PRODUCTS
            ============================================================ */}
        <div className="form-section">
          <SectionHeader number="4" title="Products" />

          <ProductSelector
            selected={formData.products}
            onChange={(v) => setField("products", v)}
          />
        </div>

        {/* ============================================================
            SECTION 5: DOCUMENTS
            ============================================================ */}
        <div className="form-section">
          <SectionHeader number="5" title="Documents" />

          <p className="section-desc">PDF, Word, or Excel — up to 10 files.</p>

          <div
            className="lead-panel-section-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "0.85rem", fontWeight: "600" }}>
              {documents.length} document(s) attached
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowDocumentUpload(!showDocumentUpload)}
            >
              <i
                className={`fas fa-${showDocumentUpload ? "times" : "plus"}`}
              ></i>
              {showDocumentUpload ? "Close" : "Add Documents"}
            </button>
          </div>

          {showDocumentUpload && (
            <DocumentUpload
              leadId={id}
              onUploadComplete={(newDocs) => {
                setDocuments((prev) => [...prev, ...newDocs]);
                setShowDocumentUpload(false);
                refreshAfterChange();
              }}
            />
          )}

          <DocumentList
            documents={documents}
            leadId={id}
            onDelete={(docId) => {
              setDocuments((prev) =>
                prev.filter((d) => d.document_id !== docId),
              );
              refreshAfterChange();
            }}
            onChanged={async () => {
              await refreshAfterChange();
            }}
          />
        </div>

        {/* ============================================================
            SECTION 6: NOTES
            ============================================================ */}
        <div className="form-section">
          <SectionHeader number="6" title="Notes" />

          <div className="form-group">
            <label>Notes</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Add any context about this lead..."
              maxLength="2000"
            />
          </div>

          <div className="form-group" style={{ marginTop: "14px" }}>
            <label>Next Steps</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.nextSteps}
              onChange={(e) => setField("nextSteps", e.target.value)}
              placeholder="What should happen next?"
              maxLength="2000"
            />
          </div>
        </div>

        {/* ============================================================
            FORM ACTIONS
            ============================================================ */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/leads")}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSaveAndBookDemo}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-calendar-plus"></i> Save &amp; Book Demo
              </>
            )}
          </button>
          <button className="btn btn-primary" disabled={submitting}>
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Update Lead
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );

  return (
    <Layout
      title="Edit Lead"
      subtitle={`Update ${formData.company || "Lead"} information`}
    >
      {loading ? (
        <div className="edit-lead-loading">
          <LoadingSpinner message="Loading complete lead data..." />
        </div>
      ) : (
        formContent
      )}
    </Layout>
  );
}

export default EditLead;
